import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'wouter';

import getVisibleFieldsFromForm from '../lib/getVisibleFieldsFromForm';

import {
    useAnswerFormSubmission,
    useFormSubmission,
    useSubmitFormSubmission,
} from './useSubmissions';

type Answers = Record<string, unknown>;

interface ValidationError extends Error {
    getResponseData: () => { errors?: FormErrors | null } | null;
}

export function getAnswersByField(answers: FormSubmissionAnswer[] | null = null): Answers {
    return (answers || []).reduce<Answers>(
        (acc, { field, value }) => ({ ...acc, [field]: value }),
        {},
    );
}

function getValidationErrors(error: unknown): FormErrors | null {
    const { name = null } = (error || {}) as Error;
    if (name !== 'ValidationError') {
        return null;
    }
    const { errors = null } = (error as ValidationError).getResponseData() || {};
    return errors;
}

/**
 * A form answered one question at a time: each answer is saved as soon as it is given, and the
 * form is submitted, which completes it, once its last visible question is done
 */
export default function useFormSteps(form: Form) {
    const { id: formId } = form;
    const [location] = useLocation();
    const { submission = null, isLoading } = useFormSubmission(formId);
    const { data = null, completedAt = null } = submission || {};
    const { answers: submissionAnswers = null } = (data || {}) as FormSubmissionData;
    const answers = useMemo(() => getAnswersByField(submissionAnswers), [submissionAnswers]);
    const fields = useMemo(() => getVisibleFieldsFromForm(form, answers), [form, answers]);
    const [errors, setErrors] = useState<FormErrors | null>(null);
    const { answerAsync } = useAnswerFormSubmission(formId);
    const { submitAsync } = useSubmitFormSubmission(formId);

    const getUrl = useCallback(
        () => `${window.location.protocol}//${window.location.host}${location}`,
        [location],
    );

    // Resolves with the answers saved, or null when the answer is refused
    const answer = useCallback(
        (fieldName: string, value: unknown): Promise<Answers | null> =>
            answerAsync({ url: getUrl(), field: fieldName, answer: value })
                .then(({ submission: newSubmission }) => {
                    setErrors(null);
                    const { data: newData = null } = newSubmission || {};
                    return getAnswersByField((newData || {}).answers || null);
                })
                .catch((error) => {
                    const { answer: answerErrors = null } = getValidationErrors(error) || {};
                    setErrors({ [fieldName]: answerErrors || [] });
                    return null;
                }),
        [answerAsync, getUrl],
    );

    // Resolves with the errors of the questions refused, or null once completed
    const complete = useCallback(
        (newAnswers: Answers): Promise<FormErrors | null> => {
            const visibleAnswers = getVisibleFieldsFromForm(form, newAnswers).reduce<Answers>(
                (acc, { name }) =>
                    typeof newAnswers[name] !== 'undefined'
                        ? { ...acc, [name]: newAnswers[name] }
                        : acc,
                {},
            );
            return submitAsync({ url: getUrl(), ...visibleAnswers })
                .then(() => {
                    setErrors(null);
                    return null;
                })
                .catch((error) => {
                    const validationErrors = getValidationErrors(error) || {};
                    setErrors(validationErrors);
                    return validationErrors;
                });
        },
        [form, submitAsync, getUrl],
    );

    return {
        answers,
        fields,
        errors,
        isLoading,
        started: Object.keys(answers).length > 0,
        completed: submission !== null && completedAt !== null,
        answer,
        complete,
    };
}
